import { DeleteOutlined } from '@ant-design/icons'
import { CommunicationKey } from '@models/Communication'
import { sendCommunicationTest } from '@services/TestCommunicationService'
import { createTestUser, deleteTestUser } from '@services/TestUserService'
import { selectHasAnyEditing } from '@store/FormCardReducer'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getTestUsers, selectTestUsers } from '@store/TestUserReducer'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import { emailRule, requiredRule } from '@utils/rules'
import {
  isPushCommunication,
  isUserBackedRecipient,
  recipientCreationError,
  sendTestFailures,
  TestSendFailure,
  testRecipientLabel,
} from '@utils/testRecipient'
import {
  Alert,
  App,
  Button,
  Checkbox,
  Flex,
  Form,
  FormInstance,
  Input,
  Popconfirm,
  Segmented,
  Space,
  Tooltip,
  Typography,
} from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { DEFAULT_ERROR, DEFAULT_SUCCESS } from '../../../../App'
import { langList } from '../../../../factories/Utils'
import { SendTestButton } from './SendTestButton'

const { Text } = Typography

// The address input is a real form field, so the shared email rule validates it
// and a server-side refusal renders in the same place as a format error.
const RECIPIENT_FIELD = 'recipientEmail'

interface Props {
  communicationKey: CommunicationKey
  context?: Record<string, string | number>
  wizardFormInstance?: FormInstance
  bypassTestCommunicationsFlag?: boolean
  disabled?: boolean
  disabledReason?: string
}

export const SendTestModal = ({
  communicationKey,
  context,
  wizardFormInstance,
  bypassTestCommunicationsFlag,
  disabled,
  disabledReason,
}: Props) => {
  const [form] = Form.useForm()
  const { message } = App.useApp()
  const [testIsSending, setTestIsSending] = useState<boolean>(false)
  const [failures, setFailures] = useState<TestSendFailure[]>([])

  const isEditing = useAppSelector(selectHasAnyEditing())

  const testUsers = useAppSelector(selectTestUsers())
  const dispatch = useAppDispatch()

  // A push reaches a device through an account, so a free address is not a
  // candidate on that channel and is left out rather than shown as a dead end.
  const selectableTestUsers = useMemo(
    () =>
      isPushCommunication(communicationKey)
        ? testUsers.filter(isUserBackedRecipient)
        : testUsers,
    [communicationKey, testUsers]
  )

  const sendNotificationToTestUsers = async () => {
    setTestIsSending(true)
    setFailures([])

    let submitted: number[] = []

    try {
      if (!communicationKey) throw new Error('no metadata')

      // Only the fields the send needs: the address input is a staging field,
      // left filled it would fail a whole-form validation for nothing.
      const values = await form.validateFields(['testUsers', 'lang'])
      const formInstanceValues = await wizardFormInstance?.validateFields()

      submitted = values.testUsers

      await sendCommunicationTest(
        communicationKey,
        values.testUsers,
        values.lang,
        context,
        formInstanceValues
      )

      message.success(DEFAULT_SUCCESS)
      form.resetFields()
    } catch (error) {
      const undeliverable = sendTestFailures(error, submitted, DEFAULT_ERROR)

      // The selection stays put so each failure reads against the row it hit.
      setFailures(undeliverable)

      if (undeliverable.length === 0) {
        message.error(DEFAULT_ERROR)
      }

      // Rethrown so the popconfirm stays open: closing it would take the
      // failures down with it.
      throw error
    } finally {
      setTestIsSending(false)
    }
  }

  // Read from the unfiltered list: a failure names its recipient even when the
  // channel no longer offers that row.
  const failureLabel = (recipientId: number) => {
    const recipient = testUsers.find(({ id }) => id === recipientId)

    return recipient !== undefined
      ? testRecipientLabel(recipient)
      : `#${recipientId}`
  }

  const setRecipientError = (error: string) =>
    form.setFields([{ name: RECIPIENT_FIELD, errors: [error] }])

  const handleInputConfirm = async () => {
    // The format is checked here so a malformed address never reaches the API.
    let email: string
    try {
      const values = await form.validateFields([RECIPIENT_FIELD])
      email = values[RECIPIENT_FIELD]
    } catch {
      return
    }

    if (!email) return

    try {
      await createTestUser({ email })
      dispatch(getTestUsers(initialQuery))
      form.resetFields([RECIPIENT_FIELD])
    } catch (error) {
      setRecipientError(recipientCreationError(error, DEFAULT_ERROR))
    }
  }

  useEffect(() => {
    dispatch(getTestUsers(initialQuery))
  }, [dispatch])

  // A recipient the list no longer offers must not stay ticked underneath, or
  // the send would carry an id the channel refuses.
  useEffect(() => {
    const selectable = new Set(selectableTestUsers.map(({ id }) => id))
    const selected: number[] = form.getFieldValue('testUsers') ?? []
    const kept = selected.filter((id) => selectable.has(id))

    if (kept.length !== selected.length) {
      form.setFieldValue('testUsers', kept)
    }
  }, [selectableTestUsers, form])

  const resetForm = () => {
    setFailures([])
    form.resetFields()
  }

  if (disabled) {
    return (
      <Tooltip title={disabledReason}>
        <span>
          <SendTestButton
            disabled
            bypassTestCommunicationsFlag={bypassTestCommunicationsFlag}
          />
        </span>
      </Tooltip>
    )
  }

  if (isEditing) {
    return (
      <SendTestButton
        isEditing={isEditing}
        bypassTestCommunicationsFlag={bypassTestCommunicationsFlag}
      />
    )
  }

  return (
    <Popconfirm
      title="Communication test"
      icon={null}
      description={
        <Flex vertical style={{ width: 400, marginTop: 15 }}>
          <Flex vertical style={{ maxHeight: 250, overflow: 'scroll' }}>
            <Form
              form={form}
              layout="vertical"
              initialValues={{ lang: langList[0].code }}
            >
              <Form.Item
                name="lang"
                label="Select language"
                style={{ marginBottom: 20 }}
              >
                <Segmented
                  options={langList.map((lang) => ({
                    value: lang.code,
                    label: `${lang.flag} ${lang.name}`,
                  }))}
                  block
                />
              </Form.Item>

              <Form.Item label="Select users" style={{ marginBottom: 10 }}>
                <Space.Compact style={{ marginBottom: 10 }} block>
                  <Form.Item
                    className="w-full"
                    name={RECIPIENT_FIELD}
                    rules={[emailRule()]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      placeholder="Email"
                      onPressEnter={handleInputConfirm}
                    />
                  </Form.Item>
                  <Button onClick={handleInputConfirm}>Add</Button>
                </Space.Compact>

                <Form.Item name="testUsers" rules={[requiredRule()]} noStyle>
                  <Checkbox.Group className="w-full" style={{ gap: 5 }}>
                    {selectableTestUsers.map((testUser) => (
                      <Space className="w-full space-between" key={testUser.id}>
                        <Space>
                          <Checkbox value={testUser.id}>
                            {testRecipientLabel(testUser)}
                          </Checkbox>
                        </Space>
                        <Button
                          danger
                          type="text"
                          size="small"
                          onClick={() =>
                            deleteTestUser(testUser.id).then(() =>
                              dispatch(getTestUsers(initialQuery))
                            )
                          }
                        >
                          <DeleteOutlined />
                        </Button>
                      </Space>
                    ))}
                  </Checkbox.Group>
                </Form.Item>

                {failures.length > 0 && (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginTop: 10 }}
                    title="Some recipients were not reached"
                    description={
                      <Flex vertical>
                        {failures.map((failure) => (
                          <Text key={failure.recipientId}>
                            {failureLabel(failure.recipientId)}:{' '}
                            {failure.message}
                          </Text>
                        ))}
                      </Flex>
                    }
                  />
                )}
              </Form.Item>
            </Form>
          </Flex>
        </Flex>
      }
      onConfirm={sendNotificationToTestUsers}
      okText="Send"
      okButtonProps={{
        size: 'middle',
        loading: testIsSending,
      }}
      cancelButtonProps={{ size: 'middle' }}
      onOpenChange={(visible) => {
        if (!visible) {
          resetForm()
        }
      }}
    >
      <SendTestButton
        bypassTestCommunicationsFlag={bypassTestCommunicationsFlag}
      />
    </Popconfirm>
  )
}
